// Pure maths for the HOPS Rating widget (FE — #5 backlog). The widget is a thin
// SVG view; everything numeric lives here so it's unit-tested and the geometry
// can be pre-verified against real values before shipping (the viz method that
// worked for the shot/pass maps).
//
// Two inputs, both from GET /hops-distribution (opaque jsonb, validated here):
//  - percentiles: 101 rows {p:0..100, val} — the HOPS rating AT each percentile.
//    Used to turn a player's rating into a percentile rank (computePercentile).
//  - buckets: ~22 rows {n, min, max, population} — a histogram of the rating over
//    the population. Used to draw the distribution curve. `population` is a
//    fraction (0..1) = the density/height of that bucket.

export interface PercentileEntry {
  p: number;
  val: number;
}

export interface HopsBucket {
  n: number;
  min: number;
  max: number;
  population: number;
}

export interface HopsDistributionParsed {
  percentiles: PercentileEntry[];
  buckets: HopsBucket[];
}

/**
 * Validate the opaque /hops-distribution payload into typed arrays, or null if
 * it's unusable (missing/empty/wrong shape). Fail-soft: the widget shows a
 * "distribution unavailable" note rather than crashing on a bad blob.
 */
export function parseDistribution(raw: {
  percentiles: unknown;
  buckets: unknown;
} | null | undefined): HopsDistributionParsed | null {
  if (!raw) return null;
  const percentiles = Array.isArray(raw.percentiles)
    ? raw.percentiles.filter(
        (e): e is PercentileEntry =>
          !!e &&
          typeof e === "object" &&
          typeof (e as PercentileEntry).p === "number" &&
          typeof (e as PercentileEntry).val === "number",
      )
    : [];
  const buckets = Array.isArray(raw.buckets)
    ? raw.buckets.filter(
        (b): b is HopsBucket =>
          !!b &&
          typeof b === "object" &&
          typeof (b as HopsBucket).min === "number" &&
          typeof (b as HopsBucket).max === "number" &&
          typeof (b as HopsBucket).population === "number",
      )
    : [];
  if (percentiles.length === 0 && buckets.length === 0) return null;
  return { percentiles, buckets };
}

/**
 * The percentile rank of a rating: the highest p whose val the rating still
 * meets or beats. Example: rating 0.681 with p93.val=0.6807 and p94.val=0.6868
 * → 93. Ported from Catalyst's useHopsDistribution. null if we have no curve.
 */
export function computePercentile(rating: number, percentiles: PercentileEntry[]): number | null {
  if (percentiles.length === 0) return null;
  const sorted = [...percentiles].sort((a, b) => a.p - b.p);
  let result = sorted[0]!.p;
  for (const entry of sorted) {
    if (rating >= entry.val) result = entry.p;
    else break;
  }
  return result;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * The core histogram, dropping the two wide catch-all tail buckets (lowest-n and
 * highest-n). Those tails span a huge rating range but hold the accumulated ends
 * of the population, so plotting them by midpoint turns the curve UP at both
 * edges — misleading. Old Catalyst does the same (its axis runs bucket n=1..20,
 * excluding n=0 and n=21). Only trims when there are enough buckets to have real
 * tails; otherwise returns them unchanged.
 */
export function coreBuckets(buckets: HopsBucket[]): HopsBucket[] {
  if (buckets.length < 4) return buckets;
  const ns = buckets.map((b) => b.n);
  const minN = Math.min(...ns);
  const maxN = Math.max(...ns);
  return buckets.filter((b) => b.n !== minN && b.n !== maxN);
}

/** English ordinal: 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 93 → "93rd". */
export function ordinal(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  const suffix =
    abs >= 11 && abs <= 13 ? "th" : last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th";
  return `${n}${suffix}`;
}

export interface CurvePoint {
  x: number;
  y: number;
}

export interface HopsCurve {
  /** Filled area path (curve down to the baseline), for the distribution shape. */
  areaPath: string;
  /** Top edge polyline of the same curve, drawn as a brighter stroke. */
  linePath: string;
  /** SVG x of the player's rating marker (clamped into the drawn domain). */
  indicatorX: number;
  /** Baseline y (bottom of the plot) — where the indicator line ends. */
  baselineY: number;
  /** Top y of the indicator line (a small gap above the tallest bar). */
  topY: number;
  /** Rating domain actually drawn, for axis labels. */
  domainMin: number;
  domainMax: number;
  width: number;
  height: number;
}

/**
 * Turn the buckets + a player's rating into SVG geometry for the curve.
 *
 * x is VALUE-scaled by each bucket's midpoint (not index-scaled) so uneven
 * bucket widths — the wide tail buckets at each end — sit at their true rating
 * position and the rating indicator lines up with the curve. y is the bucket
 * population scaled to the plot height. Coordinates are in SVG px within
 * width×height, with `pad` insets so strokes/markers aren't clipped.
 */
export function buildHopsCurve(
  buckets: HopsBucket[],
  rating: number,
  width: number,
  height: number,
  pad = 4,
): HopsCurve | null {
  if (buckets.length === 0) return null;

  // Draw the core distribution; the wide tail buckets would distort the shape.
  const core = coreBuckets(buckets);
  const domainMin = Math.min(...core.map((b) => b.min));
  const domainMax = Math.max(...core.map((b) => b.max));
  const maxPop = Math.max(...core.map((b) => b.population));
  const span = domainMax - domainMin || 1;
  const plotW = width - 2 * pad;
  const plotH = height - 2 * pad;
  const baselineY = height - pad;

  const x = (val: number) => pad + ((clamp(val, domainMin, domainMax) - domainMin) / span) * plotW;
  const y = (pop: number) => baselineY - (maxPop === 0 ? 0 : (pop / maxPop) * plotH);

  const points: CurvePoint[] = core
    .map((b) => ({ x: x((b.min + b.max) / 2), y: y(b.population) }))
    .sort((a, b) => a.x - b.x);
  const first = points[0]!;
  const last = points[points.length - 1]!;

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${round(p.x)} ${round(p.y)}`).join(" ");
  // Close the area down to the baseline at the first/last drawn x.
  const areaPath =
    `M ${round(first.x)} ${round(baselineY)} ` +
    points.map((p) => `L ${round(p.x)} ${round(p.y)}`).join(" ") +
    ` L ${round(last.x)} ${round(baselineY)} Z`;

  return {
    areaPath,
    linePath,
    indicatorX: x(rating),
    baselineY,
    topY: pad,
    domainMin,
    domainMax,
    width,
    height,
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
